import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { userAPI } from "../../services/api";
import NurseDetailModal from "../../components/NurseDetailModal";



// Skeleton Loader Component for Nurse Cards
const NurseCardSkeleton = () => (
  <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
    <div className="flex items-start gap-6">
      {/* Profile Image Skeleton */}
      <div className="w-32 h-32 bg-gray-200 rounded-lg animate-pulse flex-shrink-0"></div>
      
      <div className="flex-1 min-w-0">
        {/* Name Skeleton */}
        <div className="h-7 bg-gray-200 rounded w-40 mb-3 animate-pulse"></div>
        
        {/* Qualifications Skeleton */}
        <div className="h-5 bg-gray-200 rounded w-32 mb-3 animate-pulse"></div>

        {/* Blue Chip Skeleton */}
        <div className="h-6 bg-gray-200 rounded w-28 mb-3 animate-pulse"></div>

        {/* Experience Skeleton */}
        <div className="h-4 bg-gray-200 rounded w-24 mb-2 animate-pulse"></div>

        {/* Languages Skeleton */}
        <div className="h-4 bg-gray-200 rounded w-28 mb-2 animate-pulse"></div>

        {/* Location Skeleton */}
        <div className="h-4 bg-gray-200 rounded w-20 mb-4 animate-pulse"></div>

        {/* Button Skeleton */}
        <div className="flex gap-2">
          <div className="h-8 bg-gray-200 rounded w-24 animate-pulse"></div>
          <div className="h-8 bg-gray-200 rounded w-20 animate-pulse"></div>
        </div>
      </div>
    </div>
  </div>
);

const NurseList = () => {
  const navigate = useNavigate();
  // State for API data
  const [nurses, setNurses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Modal state
  const [selectedNurse, setSelectedNurse] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fetch nurses data from API
  useEffect(() => {
    const fetchNurses = async () => {
      try {
        setLoading(true);
        const response = await userAPI.getNursesList(currentPage, 10);
        const nursesData = response.nurses || [];

        // Transform API data to match the expected format

        const transformedNurses = nursesData.filter((nurse) => nurse.isCertified).map((nurse, index) => {
          return {
            id: nurse._id,
            name: nurse.name,
            specialization: `${
              nurse.role === "nurse" ? "Nurse" : "Healthcare Professional"
            }`,
            languages: nurse.languages || "English",
            gender: nurse.gender || "Not specified",
            experience: nurse.experience || "Experience not specified",
            availability: "Available for booking",
            description: nurse.description || `${nurse.name} is an experienced ${nurse.role} with a focus on patient care and treatment. Specialized in healthcare and patient support.`,
            bigImage:`https://backend.adhdrapidcare.com${nurse.image}`,
            image: nurse.profileImage 
              ? (nurse.profileImage.startsWith('https') 
                  ? nurse.profileImage 
                : `https://backend.adhdrapidcare.com${nurse.profileImage}`)
                : "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=100&q=80",
            phone: nurse.phone || "Not provided",
            email: nurse.email || "Not provided",
            location: nurse.location || "Available Online",
            qualifications: nurse.qualifications || "Not Available"
          };
        });

 

        setNurses(transformedNurses);
        setTotalPages(response.pagination?.totalPages || 1);
      } catch (err) {
        console.error("Error fetching nurses:", err);
        setError("Failed to load nurses");
      } finally {
        setLoading(false);
      }
    };

         fetchNurses();
  }, [currentPage]);

  const handleViewProfile = (nurseId) => {
    navigate(`/nurse-profile/${nurseId}`);
  };

  const handleNurseCardClick = (nurse) => {
    setSelectedNurse(nurse);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedNurse(null);
  };

  const handleBookSession = (nurseId) => {
    handleCloseModal();
    navigate(`/nurses/details/${nurseId}`);
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <>
      {/* Main Content */}
      <main className="flex-grow py-12 container mx-auto px-4 sm:px-6 lg:px-8">

        {/* Error Display */}
        {error && (
          <div className="flex items-center justify-center h-64 mb-8">
            <div className="text-center">
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={handleGoBack}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold transition"
              >
                Go Back
              </button>
            </div>
          </div>
        )}

        {/* Nurse Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {loading ? (
            Array.from({ length: 4 }).map((_, index) => (
              <NurseCardSkeleton key={index} />
            ))
          ) : 
          nurses.length < 1 ? (
            <div className="col-span-full flex flex-col items-center justify-center   rounded-xl p-8 text-center">
            <h3 className="text-lg font-semibold text-gray-700">No Nurse available</h3>
            <p className="text-gray-500 mt-1 mb-4">
              Once nurse are added, you’ll see them listed here.
            </p>
          </div>
          ):
          (
            nurses.map((nurse, index) => (
               <div
                 key={nurse.id}
                 onClick={() => handleNurseCardClick(nurse)}
                 className="bg-white rounded-xl shadow-md border border-gray-200 p-6 cursor-pointer hover:shadow-lg transition-all duration-300 hover:scale-105 group"
               >
                 {/* Image and Info Layout */}
                 <div className="flex items-start gap-6">
                   {/* Image */}
                   <div className="relative flex-shrink-0">
                     <img
                       src={nurse.image}
                       alt={nurse.name}
                       className="w-32 h-32 rounded-lg  border-2 border-gray-100 group-hover:border-blue-200 transition-colors"
                     />
                     {nurse.isCertified && (
                       <div className="absolute -top-2 -right-2 bg-green-500 text-white rounded-full p-1">
                         <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                           <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                         </svg>
                       </div>
                       )}
                   </div>

                   {/* Info */}
                   <div className="flex-1 min-w-0">
                     {/* Name */}
                     <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-blue-600 transition-colors">
                       {nurse.name}
                     </h3>

                     {/* Qualifications */}
                     <p className="text-sm text-gray-600 mb-3 font-medium">
                       {nurse.qualifications}
                     </p>

                     {/* Blue Chip */}
                     <div className="inline-block px-3 py-1 text-sm font-medium bg-blue-100 text-blue-600 rounded-full mb-3">
                       ADHD Qualified
                     </div>
                   </div>
                 </div>
               </div>
            ))
          )}
        </div>

        

        {/* Pagination - Only show when not loading and no error */}
        {!loading && !error && totalPages > 1 && (
          <div className="flex justify-center items-center gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-200 text-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                ></path>
              </svg>
            </button>

            {[...Array(totalPages)].map((_, index) => {
              const pageNumber = index + 1;
              return (
                <button
                  key={pageNumber}
                  onClick={() => handlePageChange(pageNumber)}
                  className={`w-8 h-8 rounded-full text-sm font-medium transition-colors ${
                    currentPage === pageNumber
                      ? "bg-blue-500 text-white"
                      : "bg-white border border-gray-300 hover:bg-gray-100 text-gray-700"
                  }`}
                >
                  {pageNumber}
                </button>
              );
            })}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-200 text-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 5l7 7-7 7"
                ></path>
              </svg>
            </button>
          </div>
        )}
      </main>

      {/* Nurse Detail Modal */}
      <NurseDetailModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        nurse={selectedNurse}
        onBookSession={handleBookSession}
      />
    </>
  );
};

export default NurseList;